import { dividerVariants } from '@magicschool/spellbook/components/Divider';
import type { IconButtonProps } from '@magicschool/spellbook/components/IconButton';
import { IconButton } from '@magicschool/spellbook/components/IconButton';
import type { PopoverProps } from '@magicschool/spellbook/components/Popover';
import { Popover } from '@magicschool/spellbook/components/Popover';
import type { PopoverContentSlot } from '@magicschool/spellbook/components/Popover/slots';
import { Slot } from '@magicschool/spellbook/components/Slot';
import type { TextProps } from '@magicschool/spellbook/components/Text';
import { Text } from '@magicschool/spellbook/components/Text';
import { TOOLBAR_GROUP_ID_ATTRIBUTE, useToolbarOverflowContext } from '@magicschool/spellbook/components/Toolbar/useToolbarOverflow';
import type { TooltipProps } from '@magicschool/spellbook/components/Tooltip';
import { Tooltip } from '@magicschool/spellbook/components/Tooltip';
import { useIsomorphicLayoutEffect } from '@magicschool/spellbook/hooks/useIsomorphicLayoutEffect';
import { useLatestRef } from '@magicschool/spellbook/hooks/useLatestRef';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import * as ToolbarPrimitive from 'radix-ui/toolbar';
import { type ReactNode, useCallback, useEffect, useState } from 'react';

export type ToolbarGroupSlotProps = React.ComponentProps<'div'> & {
  /** Accessible name for the group. */
  label: string;
  /** Whether the group may move into the overflow panel. Set `false` to pin it in the row. */
  collapsible?: boolean;
  /** Highest collapses first; equal values collapse right to left. Defaults to the group's position. */
  collapsePriority?: number;
  /** Whether one of this group's controls is currently on, so the overflow trigger can flag it while the group is hidden. */
  active?: boolean;
  /** Set by `Toolbar` on a group in the row: its position, which its measured width is cached under. */
  [TOOLBAR_GROUP_ID_ATTRIBUTE]?: string;
};

export const ToolbarGroupSlot = Slot<ToolbarGroupSlotProps>({
  displayName: 'Toolbar.Group',
  preventOverride: true,
  renderDefault: ({ className, label, collapsible: _collapsible, collapsePriority: _collapsePriority, active: _active, ...props }) => (
    <div
      role="group"
      aria-label={label}
      data-spellbook-name="toolbar-group"
      className={cn('flex shrink-0 items-center gap-1', className)}
      {...props}
    />
  ),
});

export const ToolbarButtonSlot = Slot<React.ComponentProps<typeof ToolbarPrimitive.Button>>({
  displayName: 'Toolbar.Button',
  preventOverride: true,
  renderDefault: ({ children, asChild = true, ...props }) => (
    <ToolbarPrimitive.Button data-spellbook-name="toolbar-button" asChild={asChild} {...props}>
      {children}
    </ToolbarPrimitive.Button>
  ),
});

export const ToolbarLinkSlot = Slot<React.ComponentProps<typeof ToolbarPrimitive.Link>>({
  displayName: 'Toolbar.Link',
  preventOverride: true,
  renderDefault: ({ children, asChild = true, ...props }) => (
    <ToolbarPrimitive.Link data-spellbook-name="toolbar-link" asChild={asChild} {...props}>
      {children}
    </ToolbarPrimitive.Link>
  ),
});

export type ToolbarTooltipSlotProps = Omit<TooltipProps, 'asToggleTip'>;

export const ToolbarTooltipSlot = Slot<ToolbarTooltipSlotProps>({
  displayName: 'Toolbar.Tooltip',
  preventOverride: true,
  spellbookName: false,
  renderDefault: ({ children, className, mode = 'label', side = 'top', align = 'center', ...props }) => {
    const { slots } = useSlots(children, {
      button: { Component: ToolbarButtonSlot },
      link: { Component: ToolbarLinkSlot },
      content: { Component: ToolbarTooltipContentSlot },
    });

    // The control goes *inside* the tooltip trigger. The other way around, Radix's roving-focus
    // props land on `Tooltip`, which never forwards them to the button, and arrow keys skip it.
    return (
      <Tooltip className={className} mode={mode} side={side} align={align} asToggleTip={false} {...props}>
        <Tooltip.Trigger>{slots.button ?? slots.link}</Tooltip.Trigger>
        {slots.content && <Tooltip.Content>{slots.content}</Tooltip.Content>}
      </Tooltip>
    );
  },
});

export const ToolbarTooltipContentSlot = Slot<TextProps>({
  displayName: 'Toolbar.TooltipContent',
  preventOverride: true,
  renderDefault: (props) => <Text data-spellbook-name="toolbar-tooltip-content" variant="caption-1" element="span" {...props} />,
});

// `orientation` is omitted because Radix derives it from the toolbar's own: a horizontal toolbar
// gets vertical dividers, and the overflow panel's vertical toolbar gets horizontal ones.
export const ToolbarDividerSlot = Slot<Omit<React.ComponentProps<typeof ToolbarPrimitive.Separator>, 'orientation'>>({
  displayName: 'Toolbar.Divider',
  preventOverride: true,
  renderDefault: ({ className, decorative = true, ...props }) => (
    <ToolbarPrimitive.Separator
      data-spellbook-name="toolbar-divider"
      decorative={decorative}
      className={cn(
        dividerVariants({ variant: 'default' }),
        'data-[orientation=vertical]:h-5',
        'data-[orientation=horizontal]:w-full',
        className,
      )}
      {...props}
    />
  ),
});

export type ToolbarOverflowSlotProps = {
  /** Accessible name for the trigger and for the panel's own toolbar. */
  label?: string;
  /** Props for the trigger `IconButton`, e.g. `{ size: 'md' }` to match a larger row. */
  triggerProps?: Partial<IconButtonProps>;
  /** Visible tooltip content for the trigger. Omit for no tooltip. */
  tooltip?: ReactNode;
  /** Any `Popover` prop for the panel (`open`, `onOpenChange`, `side`, ...). */
  popoverProps?: Partial<Omit<PopoverProps, 'children'>>;
  /** Any `Popover.Content` prop for the panel (`align`, `sideOffset`, ...). Defaults to `align="end"`. */
  contentProps?: Partial<Omit<React.ComponentProps<typeof PopoverContentSlot>, 'children'>>;
  /**
   * How the panel lays out its groups. `horizontal` (the default) wraps them in rows, for icon
   * groups. `vertical` stacks them in one full-width column, read like a menu, for labelled options
   * that would otherwise leave a short last row; Up and Down then move between them.
   */
  panelOrientation?: 'horizontal' | 'vertical';
};

export const ToolbarOverflowSlot = Slot<ToolbarOverflowSlotProps>({
  displayName: 'Toolbar.Overflow',
  preventOverride: true,
  renderDefault: ({ label = 'More options', triggerProps, tooltip, popoverProps, contentProps, panelOrientation = 'horizontal' }) => {
    const { collapsedGroups, hasActiveCollapsed, rowRef } = useToolbarOverflowContext();
    // Read once when the panel opens rather than kept in sync with every resize: the row's width
    // changes per pixel of a drag, and the panel needs it only as it is positioned.
    const [rowWidth, setRowWidth] = useState<number>();
    const hasCollapsed = collapsedGroups !== null;
    const isControlled = popoverProps?.open !== undefined;
    const [ownOpen, setOwnOpen] = useState(popoverProps?.defaultOpen ?? false);
    const isOpen = isControlled ? popoverProps?.open === true : ownOpen;
    const onOpenChangeRef = useLatestRef(popoverProps?.onOpenChange);

    const changeOpen = useCallback(
      (next: boolean) => {
        if (!isControlled) setOwnOpen(next);
        onOpenChangeRef.current?.(next);
      },
      [isControlled, onOpenChangeRef],
    );

    // On the effective open rather than in `changeOpen`, which Radix never calls for a controlled open.
    useIsomorphicLayoutEffect(() => {
      if (isOpen) setRowWidth(rowRef.current?.getBoundingClientRect().width);
    }, [isOpen, rowRef]);

    // The popover unmounts with the trigger and never reports closing, so an open flag would stay
    // true and reopen the panel unasked the next time something collapses.
    useEffect(() => {
      if (!hasCollapsed && isOpen) changeOpen(false);
    }, [hasCollapsed, isOpen, changeOpen]);

    if (!hasCollapsed) return null;

    const trigger = (
      <ToolbarPrimitive.Button asChild>
        <IconButton
          name="ellipsis-vertical"
          variant="ghost"
          color="neutral"
          size="sm"
          label={label}
          data-spellbook-name="toolbar-overflow"
          {...triggerProps}
        >
          {/* Hidden from assistive tech: what "active" means belongs to the feature, which owns the label. */}
          {hasActiveCollapsed ? <IconButton.NotificationBadge variant="primary" aria-hidden /> : null}
        </IconButton>
      </ToolbarPrimitive.Button>
    );

    return (
      <Popover modal={false} {...popoverProps} open={isOpen} onOpenChange={changeOpen}>
        {tooltip ? (
          <Popover.Tooltip mode="label">
            <Popover.Trigger>{trigger}</Popover.Trigger>
            <Popover.TooltipContent>{tooltip}</Popover.TooltipContent>
          </Popover.Tooltip>
        ) : (
          <Popover.Trigger>{trigger}</Popover.Trigger>
        )}
        {/* The trigger is the anchor, so `align="end"` hangs the panel off the ellipsis rather than
            off the whole row. */}
        <Popover.Content
          align="end"
          aria-label={label}
          {...contentProps}
          className={cn('p-1', contentProps?.className)}
          style={{ maxWidth: rowWidth, ...contentProps?.style }}
        >
          {/* Its own toolbar root: Radix resolves roving focus by querying the root's DOM, so the
              portaled controls would be unreachable by keyboard under the row's root. */}
          {/* The orientation sets the arrow keys too, so a stacked panel moves with Up and Down. */}
          <ToolbarPrimitive.Root
            aria-label={label}
            orientation={panelOrientation}
            className={cn('flex gap-2', panelOrientation === 'vertical' ? 'flex-col items-stretch' : 'flex-wrap items-center')}
          >
            {collapsedGroups}
          </ToolbarPrimitive.Root>
        </Popover.Content>
      </Popover>
    );
  },
});
